import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PermissionGroup,
  PermissionGroupFilters,
  permissionGroupsService,
} from '../../services/api/permissionGroups';
import Loading from '../../components/common/Loading';
import ErrorMessage from '../../components/common/ErrorMessage';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import PermissionGroupFormModal from './PermissionGroupFormModal';
import UserAssignmentModal from './UserAssignmentModal';
import { useToast } from '../../contexts/ToastContext';
import './PermissionGroupList.css';

const PermissionGroupList: React.FC = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();

  // Data state
  const [groups, setGroups] = useState<PermissionGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Modals
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<PermissionGroup | null>(null);
  const [selectedGroupForUsers, setSelectedGroupForUsers] = useState<PermissionGroup | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    group: PermissionGroup | null;
  }>({
    isOpen: false,
    group: null,
  });
  const [deleting, setDeleting] = useState(false);

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: PermissionGroupFilters = {
        page,
        pageSize,
        q: debouncedSearch || undefined,
        isActive: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
      };
      const response = await permissionGroupsService.getAll(filters);
      setGroups(response.items);
      setTotal(response.total);
    } catch (err: any) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleCreate = () => {
    setEditingGroup(null);
    setIsFormModalOpen(true);
  };

  const handleEdit = (group: PermissionGroup) => {
    setEditingGroup(group);
    setIsFormModalOpen(true);
  };

  const handleManageUsers = (group: PermissionGroup) => {
    setSelectedGroupForUsers(group);
    setIsUserModalOpen(true);
  };

  const handleDelete = (group: PermissionGroup) => {
    setDeleteConfirm({ isOpen: true, group });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.group) return;

    setDeleting(true);
    try {
      await permissionGroupsService.delete(deleteConfirm.group.id);
      showSuccess('Permission group deleted successfully');
      fetchGroups();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Delete failed';
      showError(errorMessage);
    } finally {
      setDeleting(false);
      setDeleteConfirm({ isOpen: false, group: null });
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  if (error && groups.length === 0) {
    return <ErrorMessage error={error} onRetry={fetchGroups} />;
  }

  return (
    <div className="permission-group-list-page">
      <div className="page-header">
        <h1>{t('permissionGroups.title', 'Permission Groups')}</h1>
        <button className="button button-primary" onClick={handleCreate}>
          + {t('permissionGroups.create', 'Create Group')}
        </button>
      </div>

      <div className="filters-bar">
        <input
          type="text"
          className="search-input"
          placeholder={t('common.search') + '...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <select
          className="filter-select"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">{t('common.status')}: All</option>
          <option value="active">{t('status.active')}</option>
          <option value="inactive">{t('status.inactive')}</option>
        </select>
        <span className="results-count">
          {total} {t('permissionGroups.title', 'groups').toLowerCase()}
        </span>
      </div>

      {loading && groups.length === 0 ? (
        <Loading text={t('common.loading')} />
      ) : groups.length === 0 ? (
        <div className="empty-state">
          <p>{t('common.noData')}</p>
          <button className="button button-primary" onClick={handleCreate}>
            {t('permissionGroups.create', 'Create Group')}
          </button>
        </div>
      ) : (
        <>
          <div className="groups-table">
            <table>
              <thead>
                <tr>
                  <th>{t('permissionGroups.code', 'Code')}</th>
                  <th>{t('permissionGroups.name', 'Name')}</th>
                  <th>{t('permissionGroups.description', 'Description')}</th>
                  <th>{t('permissionGroups.permissions', 'Permissions')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => (
                  <tr key={group.id}>
                    <td>
                      <code>{group.code}</code>
                      {group.isSystem && (
                        <span className="system-badge" style={{ marginLeft: '8px' }}>
                          System
                        </span>
                      )}
                    </td>
                    <td>{group.name}</td>
                    <td>
                      <span style={{ color: '#666', fontSize: '13px' }}>
                        {group.description || '-'}
                      </span>
                    </td>
                    <td>
                      <span className="permission-count">
                        🔑 {group.permissions?.length || 0}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${group.isActive ? 'active' : 'inactive'}`}>
                        {group.isActive ? t('status.active') : t('status.inactive')}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="button-link" onClick={() => handleEdit(group)}>
                          {t('common.edit')}
                        </button>
                        <button className="button-link" onClick={() => handleManageUsers(group)}>
                          {t('permissionGroups.users', 'Users')}
                        </button>
                        <button
                          className="button-link danger"
                          onClick={() => handleDelete(group)}
                          disabled={group.isSystem}
                          title={group.isSystem ? 'Cannot delete system group' : ''}
                        >
                          {t('common.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="button button-secondary button-small"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </button>
              <span className="page-info">
                Page {page} of {totalPages}
              </span>
              <button
                className="button button-secondary button-small"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Form Modal */}
      <PermissionGroupFormModal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setEditingGroup(null);
        }}
        onSuccess={fetchGroups}
        group={editingGroup}
      />

      {/* User Assignment Modal */}
      <UserAssignmentModal
        isOpen={isUserModalOpen}
        onClose={() => {
          setIsUserModalOpen(false);
          setSelectedGroupForUsers(null);
        }}
        onSuccess={fetchGroups}
        group={selectedGroupForUsers}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, group: null })}
        onConfirm={confirmDelete}
        title={t('common.delete') + ' ' + t('permissionGroups.title', 'Permission Group')}
        message={`Are you sure you want to delete the permission group "${deleteConfirm.group?.name}"? This will remove all user assignments.`}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default PermissionGroupList;
